import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Send, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const inputStyle = {
  background: "var(--lx-surface-2)",
  borderColor: "var(--lx-border-strong)",
  color: "var(--lx-text)",
};

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    website: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim() || form.name.trim().length < 2)
      e.name = "Bitte gib deinen Namen an.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Bitte gib eine gültige E-Mail-Adresse an.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Bitte beschreibe dein Anliegen (min. 10 Zeichen).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success(res.data.message || "Nachricht gesendet!");
      setForm({ name: "", email: "", company: "", message: "", website: "" });
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Etwas ist schiefgelaufen. Bitte versuche es später erneut.";
      toast.error(typeof detail === "string" ? detail : "Senden fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="lx-noise relative overflow-hidden"
    >
      <div className="lx-hero-ambient absolute inset-0 -z-10 opacity-70" />
      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 px-4 py-20 sm:px-6 lg:grid-cols-12 lg:gap-16 lg:px-8 lg:py-28">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="lg:col-span-5"
        >
          <motion.div variants={fadeUp} className="lx-kicker mb-3">
            Kontakt
          </motion.div>
          <motion.h2
            variants={fadeUp}
            className="font-display text-2xl font-semibold tracking-tight text-[color:var(--lx-text)] sm:text-3xl lg:text-4xl"
          >
            Lass uns dein nächstes Produkt bauen.
          </motion.h2>
          <motion.p
            variants={fadeUp}
            className="mt-4 max-w-md text-base leading-relaxed text-[color:var(--lx-text-2)]"
          >
            Erzähl uns von deiner Idee. Wir melden uns innerhalb von 24 Stunden
            mit den nächsten Schritten.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-8 space-y-4">
            <a
              href="mailto:labs@liberox.ch"
              className="flex items-center gap-3 text-sm text-[color:var(--lx-text-2)] transition-colors hover:text-[color:var(--lx-text)]"
            >
              <span
                className="grid h-10 w-10 place-items-center rounded-[12px] border"
                style={{ borderColor: "var(--lx-border-strong)", color: "var(--lx-bronze-2)" }}
              >
                <Mail size={18} />
              </span>
              labs@liberox.ch
            </a>
            <a
              href="tel:+41565616762"
              data-testid="contact-phone"
              className="flex items-center gap-3 text-sm text-[color:var(--lx-text-2)] transition-colors hover:text-[color:var(--lx-text)]"
            >
              <span
                className="grid h-10 w-10 place-items-center rounded-[12px] border"
                style={{ borderColor: "var(--lx-border-strong)", color: "var(--lx-bronze-2)" }}
              >
                <Phone size={18} />
              </span>
              +41 56 561 67 62
            </a>
            <div className="flex items-center gap-3 text-sm text-[color:var(--lx-text-2)]">
              <span
                className="grid h-10 w-10 place-items-center rounded-[12px] border"
                style={{ borderColor: "var(--lx-border-strong)", color: "var(--lx-bronze-2)" }}
              >
                <MapPin size={18} />
              </span>
              Liberox GmbH · Schweiz
            </div>
          </motion.div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="lg:col-span-7"
        >
          <form
            data-testid="contact-form"
            onSubmit={onSubmit}
            noValidate
            className="lx-card p-6 sm:p-8"
          >
            {sent && (
              <div
                data-testid="contact-form-success-message"
                className="mb-6 flex items-center gap-2.5 rounded-[12px] border p-4 text-sm"
                style={{
                  borderColor: "rgba(47,191,113,0.35)",
                  background: "rgba(47,191,113,0.08)",
                  color: "#8fe0b3",
                }}
              >
                <CheckCircle2 size={18} />
                Danke! Deine Nachricht ist bei uns eingegangen.
              </div>
            )}

            {/* Honeypot (hidden from users) */}
            <input
              type="text"
              name="website"
              value={form.website}
              onChange={update("website")}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", opacity: 0 }}
            />

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <Label htmlFor="name" className="mb-2 block text-sm text-[color:var(--lx-text-2)]">
                  Name *
                </Label>
                <Input
                  id="name"
                  data-testid="contact-form-name-input"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Max Mustermann"
                  className="h-11 rounded-[12px] border"
                  style={inputStyle}
                />
                {errors.name && (
                  <p className="mt-1.5 text-xs" style={{ color: "#e88" }}>
                    {errors.name}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="email" className="mb-2 block text-sm text-[color:var(--lx-text-2)]">
                  E-Mail *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-form-email-input"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="max@firma.ch"
                  className="h-11 rounded-[12px] border"
                  style={inputStyle}
                />
                {errors.email && (
                  <p className="mt-1.5 text-xs" style={{ color: "#e88" }}>
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="company" className="mb-2 block text-sm text-[color:var(--lx-text-2)]">
                Firma
              </Label>
              <Input
                id="company"
                data-testid="contact-form-company-input"
                value={form.company}
                onChange={update("company")}
                placeholder="Musterfirma GmbH"
                className="h-11 rounded-[12px] border"
                style={inputStyle}
              />
            </div>

            <div className="mt-5">
              <Label htmlFor="message" className="mb-2 block text-sm text-[color:var(--lx-text-2)]">
                Nachricht *
              </Label>
              <Textarea
                id="message"
                data-testid="contact-form-message-textarea"
                value={form.message}
                onChange={update("message")}
                placeholder="Erzähl uns von deinem Projekt …"
                rows={5}
                className="rounded-[12px] border"
                style={inputStyle}
              />
              {errors.message && (
                <p className="mt-1.5 text-xs" style={{ color: "#e88" }}>
                  {errors.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              data-testid="contact-form-submit-button"
              disabled={loading}
              className="mt-6 h-12 w-full rounded-[12px] font-display font-medium sm:w-auto sm:px-8"
              style={{
                background: "var(--lx-green)",
                color: "var(--lx-canvas)",
                boxShadow: "0 10px 30px rgba(168,232,92,0.16)",
              }}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="mr-2 animate-spin" /> Wird gesendet …
                </>
              ) : (
                <>
                  Nachricht senden <Send size={16} className="ml-2" />
                </>
              )}
            </Button>
          </form>
        </motion.div>
      </div>
    </section>
  );
};
